import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Save, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Copy,
  Eye,
  EyeOff
} from 'lucide-react';

const AdminSections = () => {
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const { data, error } = await supabase
        .from('website_sections')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setSections(data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = () => {
    const newSection = {
      section_type: 'Text + Image',
      eyebrow: 'New Section',
      headline: 'New Headline',
      subheadline: '',
      description: 'Describe this section...',
      image_url: '',
      button_text: 'Learn More',
      button_url: '#',
      is_active: true,
      display_order: sections.length,
      isNew: true,
      tempId: Math.random().toString(36).substr(2, 9)
    };
    setSections([...sections, newSection]);
  };

  const handleChange = (id: string, field: string, value: any, isTemp = false) => {
    setSections(prev => prev.map(s => {
      const match = isTemp ? s.tempId === id : s.id === id;
      return match ? { ...s, [field]: value } : s;
    }));
  };

  const handleDelete = async (id: string, isTemp = false) => {
    if (!confirm('Are you sure you want to delete this section?')) return;
    
    if (isTemp) {
      setSections(prev => prev.filter(s => s.tempId !== id));
      return;
    }

    try {
      const { error } = await supabase.from('website_sections').delete().eq('id', id);
      if (error) throw error;
      setSections(prev => prev.filter(s => s.id !== id));
      setMessage({ type: 'success', text: 'Section deleted successfully' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleDuplicate = (section: any) => {
    const { id, created_at, updated_at, ...rest } = section;
    const duplicated = {
      ...rest,
      eyebrow: `${rest.eyebrow} (Copy)`,
      display_order: sections.length,
      isNew: true,
      tempId: Math.random().toString(36).substr(2, 9)
    };
    setSections([...sections, duplicated]);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      for (const section of sections) {
        const { isNew, tempId, ...saveData } = section;
        if (isNew) {
          const { error } = await supabase.from('website_sections').insert([saveData]);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('website_sections').update(saveData).eq('id', section.id);
          if (error) throw error;
        }
      }
      setMessage({ type: 'success', text: 'All sections saved successfully' });
      fetchSections();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-emerald" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-serif text-emerald">Homepage Sections</h1>
            <p className="text-[#59635C] font-clean mt-2">Manage dynamic content blocks on your homepage.</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleAddSection}
              className="bg-white text-emerald border border-emerald px-6 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/5 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Section
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald text-ivory px-8 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Configuration
            </button>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.id || section.tempId}
              layout
              className={`bg-white p-6 rounded-2xl border ${section.is_active ? 'border-accent/10' : 'border-gray-200 opacity-75'} shadow-sm space-y-6`}
            >
              <div className="flex items-center justify-between gap-4 border-b border-accent/5 pb-4">
                <div className="flex items-center gap-4">
                  <div className="cursor-move text-gray-400"><GripVertical className="w-5 h-5" /></div>
                  <div>
                    <span className="text-[10px] font-bold text-accent uppercase tracking-widest font-clean">Section {index + 1}</span>
                    <h3 className="font-serif text-emerald text-lg">{section.eyebrow || 'Untitled Section'}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleChange(section.id || section.tempId, 'is_active', !section.is_active, !!section.isNew)}
                    className={`p-2 rounded-lg transition-colors ${section.is_active ? 'text-emerald hover:bg-emerald/5' : 'text-gray-400 hover:bg-gray-50'}`}
                    title={section.is_active ? 'Disable Section' : 'Enable Section'}
                  >
                    {section.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleDuplicate(section)}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Duplicate Section"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(section.id || section.tempId, !!section.isNew)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Section"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Section Type</label>
                    <select
                      value={section.section_type}
                      onChange={(e) => handleChange(section.id || section.tempId, 'section_type', e.target.value, !!section.isNew)}
                      className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm"
                    >
                      <option>Text + Image</option>
                      <option>Feature Cards</option>
                      <option>Image Gallery</option>
                      <option>Testimonials</option>
                      <option>CTA</option>
                      <option>Custom Content</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Eyebrow</label>
                    <input
                      type="text"
                      value={section.eyebrow}
                      onChange={(e) => handleChange(section.id || section.tempId, 'eyebrow', e.target.value, !!section.isNew)}
                      className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Headline</label>
                    <input
                      type="text"
                      value={section.headline}
                      onChange={(e) => handleChange(section.id || section.tempId, 'headline', e.target.value, !!section.isNew)}
                      className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-4 md:col-span-2">
                  <div>
                    <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Description</label>
                    <textarea
                      value={section.description}
                      onChange={(e) => handleChange(section.id || section.tempId, 'description', e.target.value, !!section.isNew)}
                      rows={4}
                      className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Button Text</label>
                      <input
                        type="text"
                        value={section.button_text}
                        onChange={(e) => handleChange(section.id || section.tempId, 'button_text', e.target.value, !!section.isNew)}
                        className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Button URL</label>
                      <input
                        type="text"
                        value={section.button_url}
                        onChange={(e) => handleChange(section.id || section.tempId, 'button_url', e.target.value, !!section.isNew)}
                        className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          
          {sections.length === 0 && (
            <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-accent/20">
              <p className="text-[#59635C] font-clean">No dynamic sections added yet.</p>
              <button onClick={handleAddSection} className="text-emerald font-bold uppercase text-xs tracking-widest mt-4 hover:underline">
                Create your first section
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSections;
